import { faDisplay } from "@fortawesome/pro-light-svg-icons"
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons"
import {
  faGear,
  faGlobeStand,
  faPeopleGroup,
  faPhotoFilmMusic,
  faSave,
  faTriangleExclamation
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Tab, Tabs, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import { CAMPAIGN_STATUSES } from "campaign/model"
import { FormInventory } from "form/inventory/components/FormInventory"
import { FORM_SAVE_ERRORS, FORM_TABS, IForm, IFormError } from "form/model"
import { getFormActionButtons } from "form/tools/action-buttons"
import { getFormChangesCount } from "form/tools/change"
import { activateFormTab, isCreateForm } from "form/tools/common"
import { cancelForm, clearForm, saveForm } from "form/tools/save"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { PageHeader } from "ui/components/main-layout/PageHeader"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { addImportant } from "ui/tools/common"
import { Content } from "../content/Content"
import { FormLayoutHeader } from "./FormLayoutHeader"

interface IFormLayoutProps {
  isLoadingInitialData: boolean
  hasInitialDataLoadingError: boolean
  isLoading: boolean
  error: IFormError
  saveError: FORM_SAVE_ERRORS | null
  status: CAMPAIGN_STATUSES | null
  form: IForm
}

export const FormLayout = React.memo(
  ({
    isLoadingInitialData,
    hasInitialDataLoadingError,
    isLoading,
    error,
    saveError,
    status,
    form
  }: IFormLayoutProps): JSX.Element => {
    const isForecastForm = form.context.isForecast
    const isDspDealForm = form.context.isDspDeal
    const tempCampaignIsCreated = form.tempCampaignId !== NOT_SAVED_ID
    let canSave =
      !form.context.isSavingTempCampaign &&
      !form.context.isSavingCampaign &&
      tempCampaignIsCreated &&
      form.context.configuration.isValid &&
      form.context.screens.isValid &&
      form.context.geoTargets.isValid &&
      form.context.creatives.isValid
    const isCreation = isCreateForm(form)
    if (!isCreation && canSave && getFormChangesCount(form.changes) === 0) {
      canSave = false
    }
    return (
      <FlexBox
        id="form-layout"
        ai="start"
        jc="start"
        vertical
        fullHeight
        sx={{
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
          animation: "fadeIn 600ms"
        }}>
        {(isLoadingInitialData || isLoading) && <TestHiddenData id="form-is-loading" />}
        {form.context.isSavingTempCampaign ? (
          <TestHiddenData id="form-temp-campaign-is-saving" />
        ) : (
          <TestHiddenData id="form-temp-campaign-is-saved" />
        )}
        {tempCampaignIsCreated && <TestHiddenData id="form-temp-campaign-is-created" />}
        {isCreation && <TestHiddenData id="form-is-creation" />}
        {!isCreation && <TestHiddenData id="form-is-edition" />}
        {!isForecastForm && !isDspDealForm && <TestHiddenData id="form-is-campaign" />}
        {isForecastForm && <TestHiddenData id="form-is-forecast" />}
        {isDspDealForm && <TestHiddenData id="form-is-dsp-deal" />}
        {saveError && <TestHiddenData id="form-save-error" />}
        {!isLoadingInitialData && !isLoading && (
          <PageHeader
            title={translate(
              `label.${isForecastForm ? "forecast" : isDspDealForm ? "deal" : "campaign"}.${isCreation ? "creating" : "editing"}`
            )}
            actionButtons={() => getFormActionButtons(form, false)}
          />
        )}
        <FlexBox
          fullWidth
          jc="start"
          ai="start"
          vertical
          sx={{
            "height": "100%",
            "minHeight": "100%",
            "maxHeight": "100%",
            "p": "1rem",
            "pt": 0,
            "& .MuiButtonBase-root.MuiTab-root": {
              textTransform: "none",
              fontWeight: 500
            }
          }}>
          <FormLayoutHeader
            form={form}
            saveError={saveError}
            hasInitialDataLoadingError={hasInitialDataLoadingError}
            isLoadingInitialData={isLoadingInitialData}
            isLoadingForm={isLoading}
            status={status}
          />
          {!isDspDealForm && !isLoadingInitialData && !isLoading && <FormInventory />}
          {isLoadingInitialData || isLoading ? (
            <LinearLoader
              id="form-linear-loader"
              text={translate(
                isForecastForm
                  ? "forecast.single.loading"
                  : isDspDealForm
                    ? "dsp-deal.single.loading"
                    : "campaign.single.loading"
              )}
              sx={{ mt: "2rem" }}
            />
          ) : hasInitialDataLoadingError || error ? (
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: 500,
                mt: "1.5rem",
                mb: "1rem",
                color: theme => theme.palette.error.main
              }}>
              {translate(
                isForecastForm
                  ? "forecast.loading.error"
                  : isDspDealForm
                    ? "dsp-deal.loading.error"
                    : "campaign.loading.error"
              )}
            </Typography>
          ) : (
            <FlexBox
              vertical
              sx={{
                alignItems: "center",
                width: "100%",
                height: isDspDealForm ? "calc(100% - 8.5rem)" : "calc(100% - 17rem)",
                minHeight: isDspDealForm ? "calc(100% - 8.5rem)" : "calc(100% - 17rem)",
                maxHeight: isDspDealForm ? "calc(100% - 8.5rem)" : "calc(100% - 17rem)"
              }}>
              <Tabs
                value={form.activeTab}
                onChange={(_event, newTab: FORM_TABS) => activateFormTab(newTab)}
                sx={{
                  "mt": "1rem",
                  "maxWidth": "100%",
                  "borderBottom": theme => theme.palette.border,
                  "& .MuiButtonBase-root.MuiTab-root.Mui-selected p": {
                    color: theme => theme.palette.text.primary2,
                    fontWeight: 500
                  },
                  "& .MuiButtonBase-root.MuiTab-root": {
                    fontWeight: 300,
                    minWidth: "11rem"
                  },
                  "@media (max-width: 1300px)": {
                    "& .MuiButtonBase-root.MuiTab-root": {
                      minWidth: "auto"
                    }
                  },
                  "& .MuiTabs-scrollButtons svg": {
                    color: theme => theme.palette.text.primary2
                  },
                  "& .checked-icon": {
                    color: theme => theme.palette.error.main,
                    ml: ".5rem"
                  }
                }}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile>
                <Tab
                  id={`form-tab-${FORM_TABS.CONFIGURATION}`}
                  value={FORM_TABS.CONFIGURATION}
                  label={
                    <FlexBox gap="1rem" sx={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faGear} size="lg" />
                      <Typography
                        sx={{
                          color: theme =>
                            addImportant(
                              (!form.context.configuration.isValid && tempCampaignIsCreated
                                ? theme.palette.error.main
                                : theme.palette.text.primary2) as string
                            )
                        }}>
                        {translate("campaign.create.wizard.tab.configuration")}
                      </Typography>
                      {!form.context.configuration.isValid && tempCampaignIsCreated && (
                        <FontAwesomeIcon icon={faTriangleExclamation} className="checked-icon" size="lg" />
                      )}
                    </FlexBox>
                  }
                />
                <Tab
                  id={`form-tab-${FORM_TABS.GEO_TARGETS}`}
                  value={FORM_TABS.GEO_TARGETS}
                  disabled={!tempCampaignIsCreated}
                  label={
                    <FlexBox gap="1rem" sx={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faGlobeStand} size="lg" />
                      <Typography
                        sx={{
                          color: theme =>
                            addImportant(
                              !tempCampaignIsCreated
                                ? theme.palette.text.disabled
                                : ((!form.context.geoTargets.isValid
                                    ? theme.palette.error.main
                                    : theme.palette.text.primary2) as string)
                            )
                        }}>
                        {translate("campaign.geo-targets")}
                      </Typography>
                      {!form.context.geoTargets.isValid && (
                        <FontAwesomeIcon icon={faTriangleExclamation} className="checked-icon" size="lg" />
                      )}
                    </FlexBox>
                  }
                />
                <Tab
                  id={`form-tab-${FORM_TABS.SCREENS}`}
                  value={FORM_TABS.SCREENS}
                  disabled={!tempCampaignIsCreated}
                  label={
                    <FlexBox gap="1rem" sx={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faDisplay} size="lg" />
                      <Typography
                        sx={{
                          color: theme =>
                            addImportant(
                              !tempCampaignIsCreated
                                ? theme.palette.text.disabled
                                : ((!form.context.screens.isValid
                                    ? theme.palette.error.main
                                    : theme.palette.text.primary2) as string)
                            )
                        }}>
                        {translate("campaign.create.wizard.tab.screens")}
                      </Typography>
                      {!form.context.screens.isValid && (
                        <FontAwesomeIcon icon={faTriangleExclamation} className="checked-icon" size="lg" />
                      )}
                    </FlexBox>
                  }
                />
                {!isDspDealForm && (
                  <Tab
                    id={`form-tab-${FORM_TABS.AUDIENCE}`}
                    value={FORM_TABS.AUDIENCE}
                    disabled={!tempCampaignIsCreated}
                    label={
                      <FlexBox gap="1rem" sx={{ position: "relative" }}>
                        <FontAwesomeIcon icon={faPeopleGroup} size="lg" />
                        <Typography
                          sx={{
                            color: theme =>
                              !tempCampaignIsCreated ? theme.palette.text.disabled : theme.palette.text.primary2
                          }}>
                          {translate("campaign.create.wizard.tab.audience")}
                        </Typography>
                      </FlexBox>
                    }
                  />
                )}
                <Tab
                  id={`form-tab-${FORM_TABS.CREATIVES}`}
                  value={FORM_TABS.CREATIVES}
                  disabled={!tempCampaignIsCreated}
                  label={
                    <FlexBox gap="1rem" sx={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faPhotoFilmMusic} size="lg" />
                      <Typography
                        sx={{
                          color: theme =>
                            addImportant(
                              !tempCampaignIsCreated
                                ? theme.palette.text.disabled
                                : ((!form.context.creatives.isValid
                                    ? theme.palette.error.main
                                    : theme.palette.text.primary2) as string)
                            )
                        }}>
                        {translate(isDspDealForm ? "dsp-deal.format" : "campaign.create.wizard.tab.creatives")}
                      </Typography>
                      {!form.context.creatives.isValid && (
                        <FontAwesomeIcon icon={faTriangleExclamation} className="checked-icon" size="lg" />
                      )}
                    </FlexBox>
                  }
                />
              </Tabs>
              <Content />
              <FlexBox jc="end" fullWidth gap sx={{ mt: "1rem" }}>
                {!isCreation ? (
                  <Button
                    id="form-cancel-button"
                    variant="outlined"
                    onClick={() => cancelForm()}
                    disabled={form.context.isSavingCampaign}>
                    {translate("button.cancel")}
                  </Button>
                ) : (
                  <Button
                    id="form-clear-button"
                    disabled={getFormChangesCount(form.changes) === 0 || form.context.isSavingCampaign}
                    variant="contained"
                    onClick={() => clearForm()}
                    startIcon={<FontAwesomeIcon icon={faTrashCan} />}>
                    {translate("button.clear")}
                  </Button>
                )}
                <Button
                  id="form-submit-button"
                  disabled={!canSave}
                  variant="contained"
                  onClick={() => saveForm()}
                  startIcon={<FontAwesomeIcon icon={faSave} />}>
                  {translate(isCreation ? "button.create" : "button.save")}
                </Button>
              </FlexBox>
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>
    )
  }
)
